/**
 * CompoundInputPure
 *
 * Pure presentational component for a compound input field with expandable values.
 * Shows a title, optional expand/collapse, and an add form when expanded.
 */
import type { ReactNode } from 'react';
import { useState } from 'react';
import { nanoid } from 'nanoid';

import { cn } from '../cn';
import { Arrows, Editor, General } from '../StrokeIcons';
import Input from '../Input2/Input';
import Button from '../Input2/Button';

// Simple email validation regex
const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const isValidEmail = (testString: string | undefined) => {
  return Boolean(EMAIL_REGEX.exec(String(testString).toLowerCase()));
};

export type CompoundInputPureProps = {
  // Display
  title?: string;
  placeholder: string;

  // State
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;

  // Field data
  hasValues?: boolean;
  hasPrimaryValue?: boolean;
  showChevron?: boolean;

  // Disabled state
  disabled?: boolean;
  disabledValue?: string;

  // Add functionality
  onAdd?: (value: string, source: string) => void;
  validateAsEmail?: boolean;

  // Children (field values)
  children?: ReactNode;
};

export function CompoundInputPure({
  title,
  placeholder,
  expanded,
  onExpandedChange,
  hasValues: _hasValues,
  hasPrimaryValue,
  showChevron,
  disabled,
  disabledValue,
  onAdd,
  validateAsEmail,
  children,
}: CompoundInputPureProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  // Submitting an empty field cancels the edit instead of firing a "Value should be correct"
  // error — there's nothing to save, so the user clearly meant "never mind."
  const cancelEdit = () => {
    setValue('');
    setError(undefined);
    onExpandedChange?.(false);
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (value == null || value.trim() === '') {
      cancelEdit();
      return;
    }
    if (validateAsEmail && !isValidEmail(value)) {
      setError('Email is incorrect');
      return;
    } else {
      setError(undefined);
    }
    onAdd?.(value, `input${nanoid()}`);
    setValue('');
    onExpandedChange?.(false);
  };

  const onFormClick = () => {
    if (disabled) return;
    setError(undefined);
    onExpandedChange?.(!expanded);
  };

  // Escape cancels the edit and clears any error.
  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancelEdit();
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className={cn('hover:bg-slate-50 dark:hover:bg-slate-800/50', 'cursor-pointer px-4 pt-2.5 pb-1', 'group')}
      onClick={onFormClick}>
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          <Editor.Pencil03 className="size-2.5 opacity-0 group-hover:opacity-60 transition-opacity" />
          <span className="text-xs font-medium uppercase tracking-wide text-haiti-500 dark:text-haiti-400">
            {title}
          </span>
          {showChevron && (
            <Arrows.ChevronDown
              className={cn('size-3.5 opacity-0 transition-all group-hover:opacity-100', { 'rotate-180': expanded })}
            />
          )}
        </div>
        {expanded && (
          <div className="flex items-center gap-2 mb-2">
            <Input
              autoFocus
              className="flex-1"
              placeholder={'+ add ' + placeholder.toLowerCase()}
              value={value}
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={onInputKeyDown}
              onChange={(e) => {
                setError(undefined);
                setValue(e.currentTarget.value);
              }}
              error={error}
            />
            <Button
              type="submit"
              size="sm"
              color="blue"
              icon={<General.Check />}
              onClick={(e) => e.stopPropagation()}
              tooltip="Save"
            />
          </div>
        )}
      </div>

      {!hasPrimaryValue && !expanded && !disabled && (
        <p className="ml-[18px] mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">Not set</p>
      )}
      {!hasPrimaryValue && !expanded && disabled && disabledValue && (
        <p className="ml-[18px] text-sm text-neutral-600 dark:text-neutral-100">{disabledValue}</p>
      )}

      {children}
    </form>
  );
}
